import React from "react";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption } from "@/components/ui/table";
import Image from "next/image";
import { Label } from "@/components/ui/label";
import { useDict } from "@/app/[lang]/providers/DictionaryProvider";
import dayjs from "dayjs";

export type ColumnDef<T> = {
  id: string;
  header: string | React.ReactNode;
  accessor: keyof T | ((row: T) => React.ReactNode);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cell?: (value: any, row: T, index: number) => React.ReactNode;

  className?: string;
  headerClassName?: string;
};

export type TableLayoutProps<T> = {
  title?: string;
  filters?: React.ReactNode;
  searchBar?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  // New props for data and columns
  data?: T[];
  columns: ColumnDef<T>[];
  emptyState?: React.ReactNode;
  onRowClick?: (row: T) => void;
  rowClassName?: string | ((row: T, index: number) => string);
  // Filter options
  dateFilterType?: 'month' | 'year-month' | 'year' | 'none';
  onDateFilterChange?: (year?: number, month?: number) => void;
  yearOptions?: number[];
  loading?: boolean;
  skeletonRows?: number;
};

export default function TableLayout<T>({
  title,
  filters,
  searchBar,
  children,
  footer,
  className,
  data = [],
  columns,
  emptyState,
  onRowClick,
  rowClassName,
  dateFilterType = 'year-month',
  onDateFilterChange,
  yearOptions,
  loading = false,
  skeletonRows = 3,
}: TableLayoutProps<T>) {
  const renderCell = (row: T, column: ColumnDef<T>, index: number) => {
    let cellContent: React.ReactNode;
    
    if (typeof column.accessor === 'function') {
      cellContent = column.accessor(row);
    } else {
      const value = row[column.accessor as keyof T] as React.ReactNode | undefined;
      cellContent = column.cell ? column.cell(value, row, index) : value;
    }

    return (
      <TableCell 
        key={`${column.id}-${index}`}
        className={column.className}
      >
        {cellContent}
      </TableCell>
    );
  };

  const getRowClassName = (row: T, index: number) => {
    const baseClass = 'hover:bg-gray-50 transition-colors';
    const customClass = typeof rowClassName === 'function' 
      ? rowClassName(row, index) 
      : rowClassName;
    
    return cn(
      baseClass,
      customClass,
      onRowClick && 'cursor-pointer'
    );
  };

  const dict = useDict();
  const currentYear = new Date().getFullYear();
  const defaultYears = Array.from({ length: 6 }, (_, i) => currentYear - i);
  const years = yearOptions && yearOptions.length > 0 ? yearOptions : defaultYears;
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  const [selectedYear, setSelectedYear] = React.useState<string>('');
  const [selectedMonth, setSelectedMonth] = React.useState<string>('');

  const handleYearChange = (value: string) => {
    if (value === 'all') {
      setSelectedYear('');
      if (onDateFilterChange) {
        onDateFilterChange(undefined, undefined);
      }
      return;
    }
    setSelectedYear(value);
    if (onDateFilterChange) {
      const year = parseInt(value);
      const month = selectedMonth ? parseInt(selectedMonth) : undefined;
      onDateFilterChange(year, month);
    }
  };

  const handleMonthChange = (value: string) => {
    setSelectedMonth(value);
    if (onDateFilterChange) {
      const year = selectedYear ? parseInt(selectedYear) : undefined;
      const month = parseInt(value);
      onDateFilterChange(year, month);
    }
  };

  const YearMonthSelects = () => {
    if (dateFilterType === 'none') {
      return null;
    }

    return (
      <div className="flex gap-4">
                {(dateFilterType === 'year-month' || dateFilterType === 'year') && (
                  <div className="flex w-full max-w-sm items-center gap-3">
                  <Label>{dict.common.year}</Label>
                  <Select value={selectedYear} onValueChange={handleYearChange}>
                    <SelectTrigger>
                      <SelectValue placeholder={dict.common.yearFilterPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{dict.common.allYears}</SelectItem>
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  </div>
                )}
                {(dateFilterType === 'year-month' || dateFilterType === 'month') && (
                  <div className="flex w-full max-w-sm items-center gap-3">
                  <Label >{dict.common.month}</Label>
                  <Select value={selectedMonth} onValueChange={handleMonthChange}>
                    <SelectTrigger>
                      <SelectValue placeholder={dict.common.monthFilterPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month) => (
                        <SelectItem key={month} value={month.toString()}>
                          {dayjs().month(month - 1).format('MMMM')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  </div>
                )}
              </div>
    );
  };

  return (
      <section className={cn("bg-white rounded-xl p-6 border", className)}>
        <h2 className="text-2xl font-bold mb-4">{title}</h2>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div className="flex gap-4">
            <YearMonthSelects />
            {filters}
          </div>
          <div className="flex-1 md:max-w-xs">{searchBar}</div>
        </div>
        <div >
          <Table >
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead 
                    key={column.id}
                    className={cn("text-left font-normal", column.headerClassName)}
                  >
                    {column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: skeletonRows }).map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {columns.map((column, colIndex) => (
                      <TableCell key={colIndex} className={column.className}>
                        <div className="h-4 bg-gray-200 rounded animate-pulse" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : data.length > 0 ? (
                data.map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    className={getRowClassName(row, rowIndex)}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((column) => 
                      renderCell(row, column, rowIndex)
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={columns.length} 
                    className="h-24 text-center text-gray-500"
                  >
                    <Image src={"/images/not-found.svg"} alt="Not Found" width={160} height={160} className="w-64 h-64 mb-8 mx-auto relative" />
                    {emptyState ||dict.common['no-data-available']}
                  </TableCell>
                </TableRow>
              )}
              {children}
            </TableBody>
          </Table>
        </div>
        {footer && <div className="mt-4">{footer}</div>}
      </section>
  );
}

// Re-export table parts for easy use
export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
